import { PROFESSIONALS } from 'navigation/CONSTANTS'
import { Dispatch, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from 'reduxStore/store'
import { handler, useToast } from 'services/context/toast'
import {
  addProfessionalInEstablishment,
  deleteEstablishmentProfessionalRelation
} from 'services/establishment'
import {
  patchProfessional,
  postUser,
  searchProfessionalsByEmail
} from 'services/user'
import Storage from 'storage/auth'
import {
  IPostProfessionalEstablishmentRelation,
  IUserSession,
  IUserSessionPost
} from 'types'
import { IProssionalRoleTypeEnum } from 'types/user/enum'
import { docMasks, validateEmail, validateField } from 'utils/masks'

const fieldsNotNull: Array<keyof IUserSessionPost> = [
  'email',
  'password',
  'treatment',
  'first_name',
  'last_name',
  'professional_license',
  'professional_license_number',
  'mobile_phone'
]

const licenseNAFieldsNotNull: Array<keyof IUserSessionPost> = [
  'email',
  'password',
  'treatment',
  'first_name',
  'last_name',
  'professional_license',
  'mobile_phone'
]

type ErrorMap = Map<keyof IUserSessionPost, string>

const initUserState = {
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  treatment: '',
  mobile_phone: '',
  professional_license: 'CRM',
  professional_license_number: '',
  specialization_name: '',
  specialization_number: ''
}

const useNewProfessional = () => {
  const [user, setUser] = useState<IUserSessionPost>(initUserState)
  const [userCopy, setUserCopy] = useState<IUserSessionPost>(initUserState)

  const [professionalId, setProfessionalId] = useState<string>()

  const [enableNewProfessional, setEnableNewProfessional] =
    useState<boolean>(false)

  const [state, setState] = useState<IStateStdTypes>('idle')

  const [countryState, setCountryState] = useState<string>('')

  const [countryStateCopy, setCountryStateCopy] = useState<string>('')

  const countryStateController: [string, Dispatch<string>] = [
    countryState,
    setCountryState
  ]

  const [errorsMap, setErrorsMap] = useState<ErrorMap>(new Map())

  const [editMode, setEditMode] = useState<boolean>(false)

  const [deletionState, setDeletionState] = useState<IStateStdTypes>('idle')
  const deletionInProgress = useRef(false)

  const establishmentId = useAppSelector(
    rState => rState.establishment.data?.establishment_id
  )

  const toast = useToast()

  const navigate = useNavigate()

  const location = useLocation()

  const currentUser = Storage.user.get()

  const disableFields =
    !editMode && (state == 'idle' || state == 'loading' || !!professionalId)

  let buttonLabel = 'Editar colaborador'

  if (!editMode) {
    if (professionalId) {
      buttonLabel = 'Vincular colaborador'
    } else {
      buttonLabel = 'Cadastrar colaborador'
    }
  }

  const clearErrorsMap = () => {
    errorsMap.clear()
    setErrorsMap(new Map())
  }

  const clearProfessional = (email?: string) => {
    setUser({
      email: email ?? '',
      first_name: '',
      last_name: '',
      password: '',
      treatment: '',
      mobile_phone: '',
      professional_license: 'CRM',
      professional_license_number: '',
      specialization_name: '',
      specialization_number: ''
    })
    setProfessionalId(undefined)
    setCountryState('')
    setCountryStateCopy('')

    clearErrorsMap()
  }

  const setFoundUser = (user: any, isEdit: boolean = false) => {
    const professionalLicense = user?.professional_license_number ?? ''
    const formatedUser = {
      ...user,
      professional_license_number: professionalLicense.slice(0, -2)
    }
    setUser(formatedUser)
    setProfessionalId(user['professional_id'])
    const state = professionalLicense.slice(-2)
    setCountryState(state)
    if (isEdit) {
      setUserCopy(formatedUser)
      setCountryStateCopy(state)
    }
  }

  const searchProfessional = async () => {
    const newErrorsMap = new Map(errorsMap)
    if (!validateEmail(user.email)) {
      if (!newErrorsMap.has('email')) {
        newErrorsMap.set('email', 'Email inválido')
      }
      setErrorsMap(newErrorsMap)
      return
    } else if (newErrorsMap.has('email')) {
      newErrorsMap.delete('email')
      setErrorsMap(newErrorsMap)
    }

    setState('loading')
    const response = await searchProfessionalsByEmail(user.email)

    if (response.success) {
      const result = response.data
      setFoundUser(result)
      clearErrorsMap()

      setState('success')
    } else if (
      response.errors?.some(error =>
        ['not found', 'professional not found'].includes(
          error?.detail?.toLowerCase()
        )
      )
    ) {
      if (professionalId) {
        clearProfessional(user.email)
      } else {
        setUser({ ...user, email: user.email })
      }
      setState('success')
    } else {
      setState('error')
    }
  }

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = event => {
    if (event.key === 'Enter') {
      ;(event.target as any).blur()
    }
  }

  const validateForm = (): ErrorMap => {
    const newErrorsMap: ErrorMap = new Map()

    const {
      email,
      password,
      first_name,
      last_name,
      professional_license,
      professional_license_number,
      specialization_name,
      specialization_number,
      mobile_phone
    } = user

    if (!validateEmail(email)) {
      newErrorsMap.set('email', 'Email inválido')
    }
    if (password.length < 6) {
      newErrorsMap.set('password', 'Senha precisa ter 6 ou mais caracteres')
    }
    if (first_name.length < 3) {
      newErrorsMap.set('first_name', 'Digite um nome válido')
    }
    if (last_name.length < 3) {
      newErrorsMap.set('last_name', 'Digite um sobrenome válido')
    }
    if (
      professional_license !== 'NA' &&
      !validateField(
        `${professional_license_number ?? ''}${countryState}`,
        docMasks[professional_license ?? ''].regexp
      )
    ) {
      newErrorsMap.set(
        'professional_license_number',
        `${professional_license} inválido`
      )
    }
    if (mobile_phone.length !== 15) {
      newErrorsMap.set('mobile_phone', 'Número de telefone inválido')
    }
    if (specialization_name == '' && specialization_number != '') {
      newErrorsMap.set('specialization_name', 'Insira o nome da especialização')
    }
    if (specialization_number != '' && specialization_number?.length != 5) {
      newErrorsMap.set(
        'specialization_number',
        'Número de especialização inválido'
      )
    }

    errorsMap.clear()

    setErrorsMap(newErrorsMap)

    return newErrorsMap
  }

  const getFormattedValue = (
    key: keyof IUserSessionPost,
    value: any,
    state?: string
  ) => {
    if (key === 'mobile_phone') {
      return value?.toString()?.replaceAll(/[^\d]/g, '')
    }
    if (
      key === 'professional_license_number' &&
      user.professional_license !== 'NA' &&
      state !== undefined
    ) {
      return value + state
    }
    return typeof value === 'string' ? value.trim() : value
  }

  const handleEditModeFormat = (): IUserSessionPost => {
    const newData: IUserSessionPost = {} as IUserSessionPost
    for (const index in user) {
      const key = index as keyof IUserSessionPost
      let origin: any
      let value: any

      if (key === 'mobile_phone') {
        origin = userCopy[key]?.toString()?.replaceAll(/[^\d]/g, '')
        value = user[key]?.toString()?.replaceAll(/[^\d]/g, '')
      } else if (
        key === 'professional_license_number' &&
        user.professional_license !== 'NA'
      ) {
        origin = userCopy[key] + countryStateCopy
        value = user[key] + countryState
      } else {
        origin = userCopy[key]
        value = user[key]
      }

      if (origin !== value) {
        if (key === 'professional_license_number') {
          newData['professional_license'] = user['professional_license']
        }
        newData[key] = getFormattedValue(
          key,
          user[key],
          key === 'professional_license_number' ? countryState : undefined
        ) as never
      }
    }
    return newData
  }

  const handleCreateModeFormat = (): IUserSessionPost => {
    const newData: IUserSessionPost = {} as IUserSessionPost
    for (const index in user) {
      const key = index as keyof IUserSessionPost
      const value = user[key]
      const current = typeof value === 'string' ? value.trim() : value
      if (key === 'mobile_phone') {
        newData[key] = current?.replaceAll(/[^\d]/g, '') as any
      } else if (
        key === 'professional_license_number' &&
        user.professional_license !== 'NA'
      ) {
        newData[key] = (current ?? '') + countryState
      } else {
        newData[key] = (current === '' ? undefined : current) as any
      }
    }
    return newData
  }

  const formatData = (): IUserSessionPost => {
    return editMode ? handleEditModeFormat() : handleCreateModeFormat()
  }

  const linkProfessional = async () => {
    if (!professionalId || !establishmentId) return

    const data: IPostProfessionalEstablishmentRelation = {
      establishment_id: establishmentId ?? '',
      professionals: [professionalId],
      role_type: user.role_type
    }

    const response = await addProfessionalInEstablishment(data)

    let state: IStateStdTypes
    let message: string | undefined

    if (response.success && response.data) {
      message = 'Colaborador adicionado com sucesso!'
      clearProfessional()
      state = 'success'
    } else if (
      response.errors?.some(
        error =>
          error?.detail?.includes('healthcare_professional_relation_pkey') &&
          (error?.detail?.includes('duplicate key value') ||
            error?.detail?.includes('duplicar valor da chave'))
      )
    ) {
      message = 'Colaborador já foi adicionado na organização!'
      clearProfessional()
      state = 'idle'
    } else {
      message = 'Não foi possível adicionar o colaborador na organização!'
      state = 'error'
    }

    if (message) {
      toast.handler({
        content: message,
        severity: state === 'idle' ? 'info' : state
      })
    }
    setState(state)
    return state === 'success'
  }

  const addNewProfessional = async () => {
    const errors = validateForm()

    if (errors.size == 0) {
      setState('loading')
      const response = await postUser(formatData(), establishmentId ?? '')

      if (response.success) {
        handler({
          content: 'Profissional cadastrado com sucesso',
          severity: 'success'
        })
        setState('success')
        return true
      } else {
        if (
          response.errors?.some(
            error =>
              error?.detail?.includes(
                'idx_registration_professional_license_number'
              ) &&
              (error?.detail?.includes('duplicate key value') ||
                error?.detail?.includes('duplicar valor da chave'))
          )
        ) {
          handler({
            content: `${user.professional_license} já cadastrado`,
            severity: 'error'
          })
        } else {
          handler({
            content: 'Não foi possível cadastrar profissional',
            severity: 'error'
          })
        }
        setState('error')
      }
    }
    return false
  }

  const editProfessional = async () => {
    if (!professionalId || !establishmentId) return
    setState('loading')

    const formatedData = formatData()

    const response = await patchProfessional(professionalId, formatedData, {
      establishment: establishmentId
    })

    let state: IStateStdTypes
    let message: string | undefined

    if (response.success && response.data) {
      message = 'Colaborador atualizado com sucesso!'
      setUserCopy(user)
      setCountryStateCopy(countryState)
      state = 'success'
    } else {
      message = 'Não foi possível atualizar o colaborador na organização!'
      state = 'error'
    }

    if (message) {
      toast.handler({
        content: message,
        severity: state
      })
    }
    setState(state)
    return state === 'success'
  }

  const handleDeleteProfessional = async (): Promise<boolean> => {
    if (
      !editMode ||
      !professionalId ||
      !establishmentId ||
      state === 'loading' ||
      deletionInProgress.current
    ) {
      return false
    }

    deletionInProgress.current = true
    setDeletionState('loading')

    let removed: boolean

    try {
      const response = await deleteEstablishmentProfessionalRelation(
        professionalId,
        establishmentId
      )
      removed = response.success
    } catch {
      removed = false
    } finally {
      deletionInProgress.current = false
    }

    setDeletionState(removed ? 'success' : 'error')

    toast.handler({
      content: removed
        ? 'Colaborador removido com sucesso!'
        : 'Houve um erro ao tentar remover o colaborador',
      severity: removed ? 'success' : 'error'
    })

    if (removed) {
      navigate(`/${PROFESSIONALS}`, { replace: true })
    }

    return removed
  }

  const choseOperation = () => {
    if (editMode) return editProfessional

    return professionalId ? linkProfessional : addNewProfessional
  }

  const onSubmit = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault()
    event.stopPropagation()
    if (state == 'loading') return

    const operation = choseOperation()

    if (await operation()) {
      navigate(`/${PROFESSIONALS}`, {
        replace: true
      })
    }
  }

  const resetInputError = (key: keyof IUserSessionPost) => {
    if (errorsMap.has(key)) {
      errorsMap.delete(key)
      setErrorsMap(new Map(errorsMap))
    }
  }

  const change = (key: keyof IUserSessionPost, value: any) => {
    const newData: IUserSessionPost = {
      ...user,
      [key]: value
    }

    if (key == 'professional_license') {
      newData.professional_license_number = ''
      errorsMap.delete('professional_license_number')
    }

    resetInputError(key)
    setUser(newData)
  }

  const setIsAdmin = (checked: boolean) => {
    setUser({
      ...user,
      role_type: checked
        ? IProssionalRoleTypeEnum.administrator
        : IProssionalRoleTypeEnum.regular
    })
  }

  // Helper to check if required fields are filled
  const areRequiredFieldsFilled = (
    fields: Array<keyof IUserSessionPost>,
    userObj: IUserSessionPost
  ) => !fields.some(key => userObj[key] === '')

  // Helper to check if professional_license_number is required and filled
  const isLicenseNumberValid = (
    userObj: IUserSessionPost,
    countryStateVal: string
  ) =>
    userObj.professional_license === 'NA' ||
    (userObj.professional_license !== 'NA' &&
      userObj.professional_license_number !== '' &&
      countryStateVal !== '')

  // Helper to check if any field has changed
  const hasUserChanged = (
    userObj: IUserSessionPost,
    userCopyObj: IUserSessionPost,
    countryStateVal: string,
    countryStateCopyVal: string
  ) => {
    if (countryStateVal !== countryStateCopyVal) return true
    for (const index in userObj) {
      const key = index as keyof IUserSessionPost
      let origin: any
      let value: any

      if (key === 'mobile_phone') {
        origin = userCopyObj[key]?.replaceAll(/[^\d]/g, '')
        value = userObj[key]?.replaceAll(/[^\d]/g, '')
      } else {
        origin = userCopyObj[key]
        value = userObj[key]
      }

      if (origin !== value) {
        return true
      }
    }
    return false
  }

  const enableButton = () => {
    if (editMode) {
      if (
        !areRequiredFieldsFilled(licenseNAFieldsNotNull, user) ||
        !isLicenseNumberValid(user, countryState)
      ) {
        return false
      }
      return hasUserChanged(user, userCopy, countryState, countryStateCopy)
    }
    return enableNewProfessional
  }

  useEffect(() => {
    setEnableNewProfessional(
      user.professional_license !== 'NA'
        ? countryState != '' && !fieldsNotNull.some(key => user[key] == '')
        : !licenseNAFieldsNotNull.some(key => user[key] == '')
    )
  }, [user, countryState])

  useEffect(() => {
    const user: IUserSession | null | undefined = location.state

    if (user) {
      setFoundUser(user, true)
    }

    setEditMode(!!user)
  }, [])

  return {
    user,
    isAdmin: user.role_type == IProssionalRoleTypeEnum.administrator,
    setIsAdmin,
    editMode,
    deletionState,
    handleDeleteProfessional,
    onSubmit,
    change,
    buttonLabel,
    searchProfessional,
    handleKeyDown,
    enableNewProfessional,
    countryStateController,
    errorsMap,
    state,
    disableFields,
    professionalId,
    disableAdminFlag:
      (currentUser?.professional_id == professionalId && editMode) ||
      state == 'loading',

    enableButton: enableButton()
  }
}

export default useNewProfessional
